import React from "react";
import { PageHero, CtaBanner, Timeline } from "../pages/PageShell";
import SectionHead from "../ui/SectionHead";
import Reveal from "../ui/Reveal";
import { Search, Map, Wrench, Truck } from "../ui/Icons";
import { Link as RouterLink } from "react-router-dom";
import { BuildLoopDiagram } from "../ui/Diagrams";
import { useSeo } from "../../lib/seo";

const PHASES = [
  {
    title: "Discovery",
    content:
      "Assess your business landscape to identify where computer vision can deliver transformative results aligned with your needs.",
    outputs: ["Opportunity map", "Feasibility on sample footage", "Success metrics"],
  },
  {
    title: "Planning",
    content:
      "Architect comprehensive blueprints mapping technical solutions to strategic business objectives.",
    outputs: ["Architecture blueprint", "Hardware sizing", "Data & labelling plan"],
  },
  {
    title: "Execution",
    content:
      "Build robust, scalable solutions with rigorous testing and iterative improvements.",
    outputs: ["Trained models", "Integrated pipeline", "Test reports"],
  },
  {
    title: "Delivery",
    content:
      "Deploy polished solutions with seamless integration and ongoing performance optimization.",
    outputs: ["Production rollout", "Monitoring & retraining", "Support"],
  },
];

const ICONS = [Search, Map, Wrench, Truck];

export default function BuildSolutions() {
  useSeo({
    title: "Build Solutions: custom computer vision and AI agents | Episcope AI",
    description:
      "Custom computer-vision models and AI-agent workflows from discovery to production on your hardware. Four phases, one accountable team.",
    path: "/build-solutions",
  });
  return (
    <div className="page">
      <div className="container">
        <PageHero
          eyebrow="Build Solutions"
          title="Custom AI ecosystems for problems no off-the-shelf model solves."
          lead="From a rough idea to a production model on your hardware. We design, train and deploy computer-vision systems tailored to your organization's unique challenges."
          chips={["Custom models", "Your data", "Your hardware", "End-to-end"]}
        />

        <div className="stack">
          <section>
            <SectionHead
              eyebrow="Process"
              title="Four phases, one accountable team."
              lead="Every phase ends with something you can evaluate, so there are no surprises at delivery."
            />
            <div className="fgrid4" style={{ marginBottom: 32 }}>
              {PHASES.map((p, i) => {
                const Icon = ICONS[i];
                return (
                  <Reveal key={p.title} className="feature card" delay={i * 0.06}>
                    <span className="icon-box">
                      <Icon />
                    </span>
                    <span className="mono muted" style={{ fontSize: "0.66rem", letterSpacing: "0.14em" }}>
                      PHASE 0{i + 1}
                    </span>
                    <h3 className="feature__title">{p.title}</h3>
                  </Reveal>
                );
              })}
            </div>
            <Timeline items={PHASES} />
          </section>

          <section className="split">
            <div>
              <SectionHead
                eyebrow="How the model gets good"
                title="A loop, not a hand-off."
                lead="Production models are never finished. Hard cases from the field feed the next training round, and every deployment ships with monitoring so drift is caught before your operators notice."
              />
              <div className="chips">
                {["Active learning", "Edge-quantized models", "Drift monitoring", "Retraining SLAs", "LLM & agent layers"].map((c) => (
                  <span key={c} className="chip">
                    {c}
                  </span>
                ))}
              </div>
            </div>
            <BuildLoopDiagram />
          </section>

          <section>
            <SectionHead
              eyebrow="Already have cameras?"
              title="If the infrastructure exists, start with Equip Intelligence."
              lead="Build Solutions is for new capabilities. When you already run CCTV, a VMS or an edge fleet, our upgrade path adds intelligence without replacing what works."
            />
            <RouterLink to="/equip-intelligence" className="btn btn-ghost">
              See Equip Intelligence
            </RouterLink>
          </section>
        </div>

        <CtaBanner
          title="Have a problem a camera could solve?"
          text="Describe it in a sentence. We will tell you honestly whether computer vision is the right tool, and what it would take."
        />
      </div>
    </div>
  );
}
